import { useRef } from 'react';

import { useDailySales } from '../../../api/daily-sales';
import { useViewport } from '../../../hooks/useViewport';
import { DashboardsPage } from '../DashboardsPage';
import { DashboardPageHeader } from '../primitives/DashboardPageHeader';
import { BucketLoading } from '../regional/BucketStates';

import { DailySalesBody } from './components/DailySalesBody';
import { DailySalesFailure } from './components/DailySalesFailure';
import { DailySalesRangePicker } from './components/DailySalesRangePicker';
import { rangeLabel } from './daily-sales-format';
import { defaultRange } from './daily-sales-range';
import { useDailySalesRange } from './use-daily-sales-range';

import './daily-sales.css';

export default function DailySalesPage() {
  const { isPhone } = useViewport();
  const { today, range, preset, choosePreset, setRange } = useDailySalesRange();
  const { data, isLoading, isError, error, isPlaceholderData, refetch } = useDailySales(range);
  const pickerRef = useRef<HTMLDivElement>(null);
  const report = isError ? undefined : data;

  const summary = isPlaceholderData
    ? `Loading ${rangeLabel(range ?? defaultRange(today))}…`
    : report
      ? rangeLabel(report)
      : '';

  // The reset button disappears with the message it sits in, so focus moves to the
  // date choice it just made rather than falling back to the top of the page.
  const resetToDefault = () => {
    setRange(null);
    requestAnimationFrame(() =>
      pickerRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]')?.focus(),
    );
  };

  return (
    <DashboardsPage header={<DashboardPageHeader title="Daily sales" isPhone={isPhone} />}>
      <div ref={pickerRef}>
        <DailySalesRangePicker
          today={today}
          range={range}
          preset={preset}
          summary={summary}
          onPreset={choosePreset}
          onApply={setRange}
        />
      </div>
      {isLoading && <BucketLoading />}
      {isError && (
        <DailySalesFailure error={error} onReset={resetToDefault} onRetry={() => refetch()} />
      )}
      {report && (
        <div
          className={isPlaceholderData ? 'nb-ds-report nb-ds-stale' : 'nb-ds-report'}
          aria-busy={isPlaceholderData || undefined}
        >
          <DailySalesBody report={report} />
        </div>
      )}
    </DashboardsPage>
  );
}
